import { ShareBarCell } from "@similarweb/pro-logic-components";
import type { AnyObject, ColumnsType } from "@similarweb/pro-logic-components";
import { Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { formatShare } from "app/filters/ngFilters";
import { allTrackers } from "app/services/track/track";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getFieldsForSwitcherOption, switcherToDataIndexMap } from "../shared/hooks/useSwitcherLogic";
import type { SwitcherOption } from "../shared/ShareTypeSwitcher";
import { NO_CELL, PreviewCell } from "../shared/TableComponents";
import type { AdIntelligenceController } from "../shared/types/types";
import { NA_VALUE } from "../utils/utils";

const PREVIEW_CELL_SPAN = { rowSpan: 5, colSpan: 1 };

export const getCompareColumns = (
    isPdf: string,
    isPreview: boolean,
    resource: AdIntelligenceController,
    selectedSwitcherOption: SwitcherOption,
    onSeeAllClick?: () => void,
    rowCount?: number,
    hasAdIntelBeta?: boolean,
) => {
    const columns: ColumnsType<AnyObject> = [
        {
            title: () => translate(`ad.intelligence.overview.top.${resource}s.column.title`),
            dataIndex: resource,
            width: isPdf === "true" ? 220 : 232,
            render: ({ url, favicon }: Record<string, string> = {}) => {
                const getWebsiteLink = (key: string) => {
                    if (resource === "publisher") {
                        return publisherAnalysisNavigation.overview.getLink({
                            ...proNavigator.getParams(),
                            key,
                        });
                    }
                    return displayAdvertisingNavigation.overview.getLink({ ...proNavigator.getParams(), key });
                };
                return url ? (
                    <CoreWebsiteCell
                        domain={url}
                        icon={favicon}
                        textAndIconMargin="8px"
                        showToolTip={!isPreview}
                        internalLink={getWebsiteLink(url)}
                        trackInternalLink={(e) => {
                            e.stopPropagation();
                            allTrackers.dispatchTrackEvent("Internal Link", "click", `Table/${url}`);
                        }}
                        childrenCount={0}
                        spacingDomainAndAddons={false}
                        TooltipComponent={({ children }) => (
                            <Tooltip
                                content={translate("ad.intelligence.overview.publishers.table.tooltip", {
                                    site: url,
                                })}
                                placement="top"
                            >
                                {children}
                            </Tooltip>
                        )}
                    />
                ) : null;
            },
        },
        {
            title: translate(
                `ad.intelligence.advertiser.publisher.compare.${switcherToDataIndexMap[selectedSwitcherOption]}.competitive.share.column.title`,
            ),
            tooltipTitle: translate(
                `ad.intelligence.advertiser.publisher.compare.${switcherToDataIndexMap[selectedSwitcherOption]}.competitive.share.column.tooltip.title`,
            ),
            tooltipSubtitle: translate(
                `ad.intelligence.advertiser.publisher.compare.${switcherToDataIndexMap[selectedSwitcherOption]}.competitive.share.column.tooltip`,
            ),
            dataIndex: getFieldsForSwitcherOption(selectedSwitcherOption).dataField,
            key: "competitiveShare",
            displayName: translate(
                `ad.intelligence.advertiser.publisher.compare.${switcherToDataIndexMap[selectedSwitcherOption]}.competitive.share.column.title`,
            ),
            onCell: (_, index) =>
                isPreview &&
                !hasAdIntelBeta &&
                getFieldsForSwitcherOption(selectedSwitcherOption).dataField !== "visits"
                    ? index === 0
                        ? PREVIEW_CELL_SPAN
                        : NO_CELL
                    : undefined,
            render: (value: { value?: Record<string, number> } | undefined, _, index: number) => {
                const dataField = getFieldsForSwitcherOption(selectedSwitcherOption).dataField;
                if (isPreview && !hasAdIntelBeta && dataField !== "visits" && index === 0) {
                    return (
                        <PreviewCell
                            onButtonClick={onSeeAllClick}
                            buttonText={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.button`)}
                            title={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.title`)}
                            subtitle={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.subtitle`)}
                            rowCount={rowCount}
                            columnCount={1}
                            isCompareMode
                            compareCount={proNavigator.getParams().key.split(",").length}
                        />
                    );
                }
                if (isPreview && !hasAdIntelBeta && dataField !== "visits") {
                    return null;
                }

                const siteList = (proNavigator.getParams().key as string).split(",");
                const data = siteList.map((site) => value?.value?.[site]);

                return value?.value ? (
                    <ShareBarCell
                        values={data}
                        tooltip={{
                            title: translate(
                                `ad.intelligence.advertiser.publisher.compare.${switcherToDataIndexMap[selectedSwitcherOption]}.widgetPopupTooltip`,
                            ),
                            labels: siteList,
                        }}
                        valueFormatter={(val) => formatShare(val, 1, 0.01, NA_VALUE) as string}
                    />
                ) : (
                    <Flex justifyContent="flex-start">{NA_VALUE}</Flex>
                );
            },
        },
    ];

    return columns;
};
